import { Theme, ThemeConsumer } from '@rneui/themed';
import React, { useState, useMemo, useEffect } from 'react';
import {
	View,
	TouchableOpacity,
	StyleSheet,
	Image,
	FlatList,
	LayoutChangeEvent,
	useWindowDimensions,
	RefreshControl,
	ActivityIndicator,
	Pressable,
	Platform,
} from 'react-native';
import { ZenEmptyComponent, ZenIcon, ZenLoader, ZenText, ZenToast } from 'zen-core-elements';

import { config } from '../../../shared/environment';
import CommonService from '../../../shared/services/commonService';

import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getCategoryProductsForSellScreen } from '../../../redux/slices/sellScreenSlice';
import { useAppNavigation } from '../../../types/@react-navigation/navigation';

import { productList } from '../../sellScreen/sellScreenModule';

import ProductPlaceholder from './productPlaceholder';
import LocationModal from './locationModal';
import CategorySelector from './sellscreenCategory';
import { updateCartProductQuantity } from '../../../redux/slices/newSellscreenSlice';
import { shallowEqual } from 'react-redux';
import MobileScanner from './mobileScanner';
import { CustomSearchBar } from '../customSearchBar';
import { parkSaleItem } from '../../../redux/types/sellScreenTypes';
import { addProductApi } from '../../../redux/utils/discountUtils';
import LinearGradient from 'react-native-linear-gradient';
import { showApiError } from '../../../shared/utils/handleApiError';

// --- CONSTANTS & CONFIG ---
const MIN_CARD_WIDTH = 160; // A card won't get smaller than this
const GAP = 12; // Space between cards
const LIMIT = 10


const getPrice = (priceRange: productList["priceRange"], currencySymbol: string) => {
	if (priceRange?.startPrice) {
		if (priceRange?.startPrice == priceRange?.endPrice) {
			return CommonService.currencyStandard(currencySymbol, priceRange?.startPrice?.toString())
		} else {
			return CommonService.currencyStandard(currencySymbol, priceRange?.startPrice?.toString()) + " - " + CommonService.currencyStandard(currencySymbol, priceRange?.endPrice?.toString())
		}
	}
	else if (priceRange?.price) {
		return CommonService.currencyStandard(currencySymbol, priceRange?.price?.toString())
	}
}


const ResponsiveProductCard = ({
	item,
	cardWidth,
	index,
	containerOnpress,
	addProduct,
	cartProductIds,
	updateQuantity,
	isWrite,
	sellScreenType
}: {
	item: productList,
	cardWidth: number,
	index: number
	isWrite: boolean
	containerOnpress: (id: number) => void
	addProduct: (id: number, isVariant: boolean) => void
	cartProductIds: parkSaleItem["cartProductIds"][number]
	updateQuantity: ({ variantId, quantity }: { variantId: number, quantity: number }) => void
	sellScreenType: "SELL" | "RETURN" | "REPLACEMENT"
}) => {
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
	const stock = item?.variantAndStockDetails?.multiLocationVariantStocks[0]?.stock!

	const isLowStock = stock !== -1 && stock > 0 && stock < 5;
	const [imgError, setImagError] = useState(false)

	const weightCount = item?.variantAndStockDetails?.variantCombinationDetails[0]?.weightCount || 1
	const variantId = item?.variantAndStockDetails?.variantCombinationDetails[0]?.variantId || -1
	const quantity = cartProductIds?.[variantId]?.quantity

	const isVarientProductInCart = (variantId == -1 && cartProductIds) ? Object.keys(cartProductIds).some((key) => cartProductIds?.[Number(key)]?.productId == item?.id) : false

	const isOutofStock = item?.isVariants ? false : stock === 0 || stock === null || stock === undefined || stock < item?.variantAndStockDetails?.variantCombinationDetails?.[0]?.weightCount!

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<Pressable style={[styles.productCard, { width: cardWidth }]}
						onPress={() => containerOnpress(item?.id)}>

						<View style={styles.imageContainer}>
							{(item?.featuredImage && !imgError) ? <Image source={{ uri: config?.AWSImageUrl + item?.featuredImage }}
								style={styles.productImage} resizeMode="contain"
								progressiveRenderingEnabled={true}
								onError={() => {
									setImagError(true)
								}}
							/>
								:
								<ProductPlaceholder name={item?.name} style={{ height: "100%" }} />}

							{item.isVariants && (
								<View style={styles.variantBadge}>
									<ZenText content={item.variantsCount + " variants"} style={styles.variantText} />
								</View>
							)}

							{isLowStock && (
								<View style={styles.lowStockBadge}>
									<ZenIcon name="alert-circle-outline" type='material-community' size={10} color={theme?.addCharge} />
									<ZenText content={stock + " left"} style={styles.lowStockText} />
								</View>
							)}
						</View>

						{/* Info Area */}
						<View style={styles.productInfo}>
							<ZenText content={item.name} numberOfLines={1} style={styles.productName} />
							<ZenText content={getPrice(item?.priceRange, currencySymbol)} style={styles.productPrice} />
							{/* <ZenText content={"₹" + item.price.toFixed(2)} style={styles.productPrice} /> */}

							{quantity ?
								<View style={styles?.updateQuantity}>
									<TouchableOpacity style={styles.qtyAction} onPress={() => updateQuantity({ variantId, quantity: quantity - weightCount })}>
										<ZenIcon name='minus' type='antdesign' size={16} color={theme?.blue600} />
									</TouchableOpacity>
									<ZenText content={quantity} style={styles.qtyText} />
									<TouchableOpacity style={styles.qtyAction} onPress={() => updateQuantity({ variantId, quantity: quantity + weightCount })}>
										<ZenIcon name='plus' type='antdesign' size={16} color={theme?.blue600} />
									</TouchableOpacity>
								</View>
								:
								<TouchableOpacity disabled={isOutofStock} style={isOutofStock ? styles.outOfStockBtn : styles.addToCartBtn} onPress={() => addProduct(item?.id, item.isVariants)}>
									<ZenText content={isOutofStock ? 'Out of Stock' : item.isVariants && sellScreenType !== "RETURN" ? isVarientProductInCart ? "Update" : 'Select Variant' : 'Add'} style={isOutofStock ? styles.outOfStockText : styles.addToCartText} />
								</TouchableOpacity>
							}
						</View>
					</Pressable>
				)
			}}
		</ThemeConsumer>
	);
};


function NewSellscreenProductList({ onPressDetails, isWrite }: { isWrite: boolean, onPressDetails: (id: number, isProduct?: boolean) => void }) {
	const listEmpty = require("../../../assets/no_data_image.webp")
	const [containerWidth, setContainerWidth] = useState(0);
	const { width: SCREEN_WIDTH } = useWindowDimensions();
	const navigation = useAppNavigation()
	const dispatch = useAppDispatch()
	const { value: ProductList, loading: productLoading, endReached } = useAppSelector((state) => state?.sellScreen?.sellScreenProducts)
	const { value: categoryList, loading: categoryLoading } = useAppSelector((state) => state?.sellScreen?.sellScreenCategories)
	const { id: locationId } = useAppSelector((state) => state?.common?.currentLocation)
	const cartProductIds = useAppSelector((state) => state?.NewSellscreenSlice?.cartProductIds, shallowEqual)
	const [search, setSearch] = useState<string | null>(null)
	const [prevSearch, setPrevSearch] = useState<string | null>(search)
	const [offset, setOffset] = useState<null | number>(null)
	const [selectedCategory, setSelectedCategory] = useState<number | null>(null)
	const [refresh, setRefresh] = useState(false)
	const [footerLoader, setFooterLoader] = useState(false)
	const { cartProducts, sellScreenType } = useAppSelector((state) => state?.NewSellscreenSlice)

	//useEffect triggers when the searchData is change
	useEffect(() => {
		if (prevSearch !== search) {
			const debounceSearchText = setTimeout(() => {
				toggleOffset()

			}, 500);
			return () => { clearTimeout(debounceSearchText) }
		}
	}, [search, prevSearch])

	useEffect(() => {
		if (selectedCategory !== null) {
			toggleOffset()
		}
	}, [selectedCategory])



	const toggleOffset = () => {
		setOffset((prev) => prev == -1 ? 0 : -1)

	}

	useEffect(() => {
		if (offset !== null) {
			getProductsApi()
		}
	}, [offset])

	const getProductsApi = () => {
		let payload = {
			limit: LIMIT,
			offset: (!offset || offset == -1) ? 0 : offset,
			categoryId: (!selectedCategory || selectedCategory < 0) ? "" : JSON.stringify([selectedCategory]),
			searchText: search ?? "",
			locationId: locationId
		}
		setPrevSearch(search)
		dispatch(getCategoryProductsForSellScreen({ storeId: config?.storeId, payload })).unwrap().catch(showApiError).finally(() => {
			setRefresh(false)
			setFooterLoader(false)
		})
	}



	const onEndReached = () => {
		if (!endReached) {
			let offsetValue = offset && offset > 0 ? offset : 0
			setFooterLoader(true)
			setOffset(LIMIT + offsetValue)
		}
	}



	const onRefresh = () => {
		setSearch(null)
		setSelectedCategory(null)
		setRefresh(true)
		toggleOffset()
	}

	// --- CALCULATION LOGIC ---
	const gridLayout = useMemo(() => {
		if (containerWidth === 0) return { numColumns: 2, cardWidth: 150 };

		let numColumns = Math.floor((containerWidth + GAP) / (MIN_CARD_WIDTH + GAP));

		numColumns = Math.max(1, Math.min(numColumns, 6));

		const totalGapSpace = GAP * (numColumns - 1);
		const cardWidth = (containerWidth - totalGapSpace) / numColumns;

		return { numColumns, cardWidth };
	}, [containerWidth]);

	const footerComponent = () => {
		return (
			<ThemeConsumer>
				{({ theme }) => (
					footerLoader ?
						<View>
							<ActivityIndicator size={"small"} color={theme?.primary} />
						</View >
						: undefined
				)}
			</ThemeConsumer>
		)
	}

	const addProduct = async (productId: number, isVariant: boolean) => {
		if (!isWrite) {
			ZenToast({ message: "You don't have access!", type: "warning" })
			return
		}
		if (isVariant) {
			onPressDetails(productId)
		} else {
			addProductApi({ dispatch, locationId, productId, isFromBarcode: false })
		}
	}

	const updateQuantity = ({ variantId, quantity }: { variantId: number; quantity: number }) => {
		if (!isWrite) {
			ZenToast({ message: "You don't have access!", type: "warning" })
			return
		}
		dispatch(updateCartProductQuantity({ variantId, quantity, locationId }))
	}

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<View style={styles.container}>

						<View style={styles.header}>
							<View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
								{SCREEN_WIDTH > 900 ? null : <ZenIcon name={'dots-grid'} type={"material-community"} size={SCREEN_WIDTH > 600 ? 28 : 22} color={theme?.dark} onPress={() => navigation?.openDrawer()} />}
								<ZenText content="Sell Screen" style={styles.headerTitle} />
							</View>
							{/* <LocationModal /> */}
						</View>

						<CustomSearchBar
							search={search}
							setSearch={setSearch}
							toggleOffset={toggleOffset}
							rightComponent={sellScreenType !== "RETURN" && Platform.OS !== "web" ? <MobileScanner /> : null}
							placeholder={"Search by product id, name"}
						/>
						<View style={{ paddingTop: 12 }}>
							<CategorySelector
								data={[...categoryList]}
								onCategorySelect={(id) => {
									setSelectedCategory(id)
								}}
								refresh={refresh}
							/>
						</View>
						<View pointerEvents={sellScreenType === "RETURN" ? 'none' : "auto"} style={[{ flex: 1, opacity: sellScreenType === "RETURN" ? 0.7 : 1 }, ProductList?.length ? styles.parentProductView : null]}>
							<View
								style={styles.gridWrapper}
								onLayout={(e: LayoutChangeEvent) => setContainerWidth(e.nativeEvent.layout.width)}
							>
								{containerWidth > 0 && (
									(productLoading || categoryLoading) && !refresh && !footerLoader ?
										<ZenLoader viewStyle={{ backgroundColor: "transparent", flex: 1 }} color={theme?.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} />
										: <FlatList
											key={gridLayout.numColumns}
											data={ProductList}
											extraData={cartProductIds[locationId]}
											keyExtractor={item => item?.id?.toString()}
											numColumns={gridLayout.numColumns}
											renderItem={({ item, index }) => (
												<ResponsiveProductCard
													item={item}
													cardWidth={gridLayout.cardWidth}
													index={index}
													containerOnpress={(id) => onPressDetails(id, true)}
													addProduct={addProduct}
													cartProductIds={cartProductIds[locationId]}
													updateQuantity={updateQuantity}
													isWrite={isWrite}
													sellScreenType={sellScreenType}
												/>
											)}
											ListEmptyComponent={
												<ZenEmptyComponent req={listEmpty} text='No products found' imageStyle={{ width: 200, height: 200 }} viewStyle={{ paddingBottom: 50 }} />
											}

											columnWrapperStyle={gridLayout.numColumns > 1 ? { gap: GAP } : undefined}
											contentContainerStyle={{ gap: GAP, paddingBottom: 20, flex: ProductList?.length ? undefined : 1 }}
											showsVerticalScrollIndicator={false}
											refreshControl={<RefreshControl refreshing={refresh} onRefresh={onRefresh} />}
											onEndReached={onEndReached}
											ListFooterComponent={footerComponent}
											ListFooterComponentStyle={{ height: ProductList.length ? 70 : 0 }}
										/>
								)}
							</View>
						</View>
					</View>
				)
			}}
		</ThemeConsumer >
	);
}

const style = (theme: Theme) => (
	StyleSheet.create({
		container: {
			flex: 1,
		},
		subHeader: {
			fontSize: 12,
			color: theme?.disabledGray500,
			marginBottom: 16,
		},
		gridWrapper: {
			flex: 1,
			width: '100%',
		},
		parentProductView: {
			flex: 1,
			padding: 10,
			backgroundColor: theme?.iceWhite,
		},

		// --- CARD STYLES ---
		productCard: {
			backgroundColor: theme?.light,
			borderRadius: 16,
			overflow: 'hidden',
			borderWidth: 1,
			borderColor: theme?.coolGray,
			shadowColor: theme?.disabledGray500,
			shadowOffset: { width: 0, height: 4 },
			shadowOpacity: 0.05,
			shadowRadius: 8,
			elevation: 2,
		},
		imageContainer: {
			height: 120, // Fixed height for image area
			backgroundColor: theme?.softSlate,
			alignItems: 'center',
			justifyContent: 'center',
			position: 'relative',
		},
		productImage: {
			width: "100%",
			height: "100%",
			// opacity: 0.8,
		},
		productInfo: {
			padding: 10,
		},
		productName: {
			fontSize: theme?.errorFontSize,
			fontFamily: theme?.bold,
			color: theme?.lightDark,
			marginBottom: 4,
		},
		productPrice: {
			fontSize: theme?.tinyFontSize,
			fontFamily: theme?.medium,
			color: theme?.lightDark,
			marginBottom: 8,
		},

		// Badges
		variantBadge: {
			position: 'absolute',
			top: 6,
			right: 6,
			backgroundColor: theme?.tealGreen,
			paddingVertical: 2,
			paddingHorizontal: 6,
			borderRadius: 6,
		},
		variantText: { color: theme?.light, fontSize: theme?.miniBadge, fontFamily: theme?.bold },

		lowStockBadge: {
			position: 'absolute',
			bottom: 6,
			left: 6,
			flexDirection: 'row',
			alignItems: 'center',
			backgroundColor: theme?.warmCream,
			paddingVertical: 2,
			paddingHorizontal: 6,
			borderRadius: 6,
			borderWidth: 1,
			borderColor: theme?.goldenOrange,
			gap: 2,
		},
		lowStockText: { color: theme?.goldenOrange, fontSize: theme?.miniBadge, fontFamily: theme?.bold },

		// Button
		addToCartBtn: { backgroundColor: theme?.blue50, paddingVertical: 8, borderRadius: 6, alignItems: 'center' },
		addToCartText: { color: theme?.blue600, fontSize: theme?.miniFontSize, fontFamily: theme?.medium },
		outOfStockBtn: { backgroundColor: theme?.red50, paddingVertical: 6, borderRadius: 6, alignItems: 'center' },
		outOfStockText: { color: theme?.red500, fontSize: theme?.badgeFontSize, fontFamily: theme?.medium },


		//header
		header: { flexDirection: 'row', alignItems: 'center', marginBottom: 16, justifyContent: 'space-between' },
		headerTitle: { fontSize: theme?.headerFontSize, fontFamily: theme?.bold, color: theme?.lightDark },

		updateQuantity: {
			flexDirection: "row",
			alignItems: "center",
			justifyContent: "space-between",
			paddingTop: 4,
			paddingHorizontal: 15
		},
		qtyAction: {
			backgroundColor: theme?.blue50,
			paddingHorizontal: 8,
			paddingVertical: 7,
			borderRadius: 5
		},
		qtyText: {
			fontSize: theme?.smallHeaderFontSize,
			fontFamily: theme?.bold,
			color: theme?.blue600
		},
		selectedTab: {
			backgroundColor: theme?.lightPrimary,
			borderRadius: 18,
			borderWidth: 1,
			borderColor: theme?.borderColor
		}
	}))

export default NewSellscreenProductList